/**
 * ForkEdge — teal solid spawn link rendered on the ChatMap from a fork-origin
 * turn to the Camp (spine-root marker) a peer was spawned onto.
 *
 * Distinct from ReferenceEdge (violet dashed "references"): a fork is a
 * structural spawn, not a citation — so it reads as a solid teal stroke with a
 * "spawned" hover label. (C2.1 sp4 — spawn-onto-spine made legible on the map.)
 *
 * Per SP3-MF-03: useTheme imports from `../../../../contexts/ThemeContext` and
 * destructures `darkMode` (NOT `isDark`).
 */
import React, { useState } from 'react';
import { EdgeProps, MarkerType, getBezierPath } from 'reactflow';
import { useTheme } from '../../../../contexts/ThemeContext';


export interface ForkEdgeData {
  kind: 'fork';
}


export const ForkEdge: React.FC<EdgeProps<ForkEdgeData>> = ({
  id, sourceX, sourceY, targetX, targetY,
}) => {
  const { darkMode } = useTheme();
  // Teal — distinct from the violet reference edge (dark: lighter; light: deeper).
  const stroke = darkMode ? '#5eead4' : '#0d9488';
  const [edgePath] = getBezierPath({ sourceX, sourceY, targetX, targetY });
  const [hovered, setHovered] = useState(false);

  return (
    <>
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        stroke={stroke}
        strokeWidth={2}
        fill="none"
        opacity={hovered ? 0.95 : 0.75}
        markerEnd={`url(#${MarkerType.ArrowClosed}-${stroke})`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ cursor: 'default' }}
      />
      {hovered && (
        <text style={{ pointerEvents: 'none' }}>
          <textPath
            href={`#${id}`}
            startOffset="50%"
            textAnchor="middle"
            fontSize="9"
            fill={stroke}
          >
            spawned
          </textPath>
        </text>
      )}
    </>
  );
};
